/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { useMemo } from 'react'
import { useNavigate } from 'react-router-dom'

import { EuiFormRow, EuiLink, EuiSuperSelect, EuiText } from '@elastic/eui'

import { FormattedMessage } from '@modules/i18n'
import type { TrafficFilterRulesetInfo } from '@modules/cloud-api/v1/types'
import { networkSecurityUrl } from '@modules/network-security-pages/urls'
import type { RulesetType } from '@modules/network-security-lib/types'
import type { TrafficFilterInfo } from '@modules/network-user-api/v1/types'

type Props = {
  rulesetType: RulesetType
  rulesets: Array<TrafficFilterRulesetInfo | TrafficFilterInfo>
  value: string | null
  onChange: (value: string) => void
}

const SelectTrafficFilter: React.FunctionComponent<Props> = ({
  rulesetType,
  rulesets,
  value,
  onChange,
}) => {
  const navigate = useNavigate()

  const options = useMemo(
    () =>
      rulesets.map((ruleset) => ({
        value: ruleset.id,
        inputDisplay: ruleset.name,
        dropdownDisplay: (
          <div>
            <EuiText>{ruleset.name}</EuiText>
            {ruleset.description && (
              <EuiText size='s' color='subdued'>
                <p>{ruleset.description}</p>
              </EuiText>
            )}
          </div>
        ),
      })),
    [rulesets],
  )

  return (
    <EuiFormRow
      label={
        rulesetType === 'ipFilter' ? (
          <FormattedMessage
            id='selectTrafficFilter.label.ipFilter'
            defaultMessage='Select IP filter'
          />
        ) : (
          <FormattedMessage
            id='selectTrafficFilter.label.privateConnection'
            defaultMessage='Select private connection'
          />
        )
      }
      helpText={
        <FormattedMessage
          id='selectTrafficFilter.helpText'
          defaultMessage='Network security filters can be created <link>in your account</link>.'
          values={{
            link: (content) => (
              <EuiLink
                href={networkSecurityUrl()}
                onClick={(e) => {
                  e.preventDefault()
                  navigate(networkSecurityUrl())
                }}
              >
                {content}
              </EuiLink>
            ),
          }}
        />
      }
    >
      <EuiSuperSelect
        options={options}
        valueOfSelected={value || ''}
        onChange={onChange}
        placeholder={
          <FormattedMessage id='selectTrafficFilter.placeholder' defaultMessage='Select' />
        }
        hasDividers={true}
      />
    </EuiFormRow>
  )
}

export default SelectTrafficFilter
